<script setup lang="ts">
import { computed, ref } from 'vue'
import CanvasPreviewSurface, {
  type PreviewDrawItem,
  type PreviewHotspotItem,
} from '@/components/workspace/CanvasPreviewSurface.vue'
import FontSelectField from '@/features/project-font/components/FontSelectField.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type { SettingsSaveLoadTabProps } from '@/features/workspace/settings/types'
import { SAVE_LOAD_FONT_MIN_SIZE } from '@/features/workspace/settings/saveLoadFontSettings'

const props = defineProps<SettingsSaveLoadTabProps>()

const firstPreviewSlot = computed(() => props.saveLoadPreviewSlots[0] ?? null)
const previewThumbSize = computed(() => {
  const zoom = Math.max(1, Number(props.saveLoadSetting.Zoom || 1))
  const scale = zoom / 100
  return {
    width: Math.max(1, Math.round(props.screenWidth * scale)),
    height: Math.max(1, Math.round(props.screenHeight * scale)),
  }
})

const previewDrawItems = computed<PreviewDrawItem[]>(() => {
  const items: PreviewDrawItem[] = []
  for (const slot of props.saveLoadPreviewSlots) {
    items.push({
      key: `slot-${slot.key}`,
      kind: props.saveLoadBackButtonUrl ? 'image' : 'rect',
      x: slot.x,
      y: slot.y,
      width: slot.width,
      height: slot.height,
      imageUrl: props.saveLoadBackButtonUrl,
      placeholderText: '存档按钮',
      fillColor: '#8a8764',
      strokeColor: '#d7cf99',
    })
    if (props.saveLoadSetting.IsMinPic) {
      items.push({
        key: `thumb-${slot.key}`,
        kind: props.saveLoadPreviewThumbUrl ? 'image' : 'rect',
        x: slot.x + Number(props.saveLoadSetting.Pic_X || 0),
        y: slot.y + Number(props.saveLoadSetting.Pic_Y || 0),
        width: previewThumbSize.value.width,
        height: previewThumbSize.value.height,
        imageUrl: props.saveLoadPreviewThumbUrl,
        placeholderText: '截图',
        fillColor: '#6b7a8f',
        strokeColor: '#c7d2e0',
      })
    }
    if (props.saveLoadSetting.IsMapName) {
      items.push({
        key: `name-${slot.key}`,
        kind: 'text',
        x: slot.x + Number(props.saveLoadSetting.Name_X || 0),
        y: slot.y + Number(props.saveLoadSetting.Name_Y || 0),
        width: Math.max(80, slot.width - Number(props.saveLoadSetting.Name_X || 0) - 8),
        height: 24,
        text: slot.title,
        fontFamily: props.saveLoadPreviewFont,
        fontSize: props.saveLoadPreviewFontSize,
        textColor: props.saveLoadPreviewTextColor,
      })
    }
    if (props.saveLoadSetting.IsDate) {
      items.push({
        key: `date-${slot.key}`,
        kind: 'text',
        x: slot.x + Number(props.saveLoadSetting.Date_X || 0),
        y: slot.y + Number(props.saveLoadSetting.Date_Y || 0),
        width: Math.max(120, slot.width - Number(props.saveLoadSetting.Date_X || 0) - 8),
        height: 22,
        text: slot.date,
        fontFamily: props.saveLoadPreviewFont,
        fontSize: Math.max(10, Math.round(props.saveLoadPreviewFontSize * 0.72)),
        textColor: props.saveLoadPreviewTextColor,
      })
    }
  }
  if (props.saveLoadCloseButtonUrl) {
    items.push({
      key: 'close-button',
      kind: 'image',
      style: props.saveLoadCloseButtonStyle,
      imageUrl: props.saveLoadCloseButtonUrl,
      placeholderText: '关闭按钮',
    })
  }
  if (props.saveLoadSetting.IsPage && props.saveLoadPrevButtonUrl) {
    items.push({
      key: 'prev-button',
      kind: 'image',
      style: props.saveLoadPrevButtonStyle,
      imageUrl: props.saveLoadPrevButtonUrl,
      placeholderText: '上一页',
    })
  }
  if (props.saveLoadSetting.IsPage && props.saveLoadNextButtonUrl) {
    items.push({
      key: 'next-button',
      kind: 'image',
      style: props.saveLoadNextButtonStyle,
      imageUrl: props.saveLoadNextButtonUrl,
      placeholderText: '下一页',
    })
  }
  if (props.saveLoadSetting.IsPage) {
    items.push({
      key: 'page-text',
      kind: 'text',
      style: props.saveLoadPreviewPageTextStyle,
      width: 80,
      height: 24,
      text: props.saveLoadPreviewPageText,
      fontFamily: props.saveLoadPreviewFont,
      fontSize: Math.max(12, Math.round(props.saveLoadPreviewFontSize * 0.78)),
      textColor: props.saveLoadPreviewTextColor,
    })
  }
  return items
})

const previewHotspotItems = computed<PreviewHotspotItem[]>(() => {
  const items: PreviewHotspotItem[] = [
    {
      key: 'viewport',
      style: {
        ...props.saveLoadPreviewViewportStyle,
        border: '1px dashed #f59e0b',
        cursor: 'move',
      },
      ariaLabel: '存档内容区拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('viewport', event),
      resizeHandle: {
        style: {
          position: 'absolute',
          right: '0',
          bottom: '0',
          width: '14px',
          height: '14px',
          borderLeft: '1px solid #fbbf24',
          borderTop: '1px solid #fbbf24',
          background: '#a16207',
          cursor: 'nwse-resize',
        },
        ariaLabel: '调整存档内容区大小',
        onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewResize(event),
      },
    },
    {
      key: 'close-button',
      style: {
        ...props.saveLoadCloseButtonStyle,
        cursor: 'move',
      },
      ariaLabel: '关闭按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('close', event),
    },
  ]
  if (firstPreviewSlot.value && props.saveLoadSetting.IsMapName) {
    items.push({
      key: 'name-hotspot',
      style: {
        left: `${firstPreviewSlot.value.x + Number(props.saveLoadSetting.Name_X || 0)}px`,
        top: `${firstPreviewSlot.value.y + Number(props.saveLoadSetting.Name_Y || 0)}px`,
        width: `${Math.max(80, firstPreviewSlot.value.width - Number(props.saveLoadSetting.Name_X || 0) - 8)}px`,
        height: '24px',
        cursor: 'move',
        outline: '1px dashed rgba(59,130,246,0.7)',
      },
      ariaLabel: '剧情名拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('name', event),
    })
  }
  if (firstPreviewSlot.value && props.saveLoadSetting.IsDate) {
    items.push({
      key: 'date-hotspot',
      style: {
        left: `${firstPreviewSlot.value.x + Number(props.saveLoadSetting.Date_X || 0)}px`,
        top: `${firstPreviewSlot.value.y + Number(props.saveLoadSetting.Date_Y || 0)}px`,
        width: `${Math.max(120, firstPreviewSlot.value.width - Number(props.saveLoadSetting.Date_X || 0) - 8)}px`,
        height: '22px',
        cursor: 'move',
        outline: '1px dashed rgba(16,185,129,0.7)',
      },
      ariaLabel: '时间拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('date', event),
    })
  }
  if (firstPreviewSlot.value && props.saveLoadSetting.IsMinPic) {
    items.push({
      key: 'thumb-hotspot',
      style: {
        left: `${firstPreviewSlot.value.x + Number(props.saveLoadSetting.Pic_X || 0)}px`,
        top: `${firstPreviewSlot.value.y + Number(props.saveLoadSetting.Pic_Y || 0)}px`,
        width: `${previewThumbSize.value.width}px`,
        height: `${previewThumbSize.value.height}px`,
        cursor: 'move',
        outline: '1px dashed rgba(245,158,11,0.7)',
      },
      ariaLabel: '截图拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('thumb', event),
    })
  }
  if (props.saveLoadSetting.IsPage) {
    items.push(
      {
        key: 'prev-button',
        style: {
          ...props.saveLoadPrevButtonStyle,
          cursor: 'move',
          zIndex: 4,
        },
        ariaLabel: '上一页按钮拖拽热区',
        onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('prev', event),
      },
      {
        key: 'next-button',
        style: {
          ...props.saveLoadNextButtonStyle,
          cursor: 'move',
          zIndex: 4,
        },
        ariaLabel: '下一页按钮拖拽热区',
        onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('next', event),
      },
      {
        key: 'page-text',
        style: {
          ...props.saveLoadPreviewPageTextStyle,
          width: '120px',
          height: '32px',
          cursor: 'move',
          outline: '1px dashed rgba(255,255,255,0.7)',
          zIndex: 5,
        },
        ariaLabel: '页码拖拽热区',
        onPointerdown: (event: PointerEvent) => props.startSaveLoadPreviewDrag('page', event),
      },
    )
  }
  return items
})
</script>

<template>
  <div v-if="portraitLayout" class="settings-page settings-portrait-page save-load-page-portrait">
    <section class="setting-card settings-preview-card settings-portrait-preview save-load-preview-card">
      <CanvasPreviewSurface
        host-class="settings-preview-shell save-load-preview-shell"
        stage-class="settings-preview-stage save-load-preview-stage"
        :design-width="screenWidth"
        :design-height="screenHeight"
        :background-url="saveLoadBackgroundUrl"
        background-empty-text="存读档背景图"
        :draw-items="previewDrawItems"
        :hotspot-items="previewHotspotItems"
        :guide-active="previewGuideActive"
        @stage-resize="onPreviewStageResize"
      />
    </section>

    <div class="settings-portrait-props">
      <section class="setting-card">
        <label class="field-block save-load-inline-field">
          <span class="field-label save-load-inline-label">背景图</span>
          <ResourceSelectField
            :model-value="saveLoadSetting.BackPic.name"
            category="graphic"
            :base-folder-path="graphicBaseFolderPath"
            display-mode="full"
            store-mode="relative"
            picker-title="选择存读档背景图"
            @update:model-value="updateSaveLoadBackPic"
          />
        </label>

        <div class="field-inline save-load-summary-row">
          <span class="field-label">最大存档数</span>
          <NumberStepperField v-model="saveLoadSetting.Max" class="cell-input sm save-load-inline-stepper" :min="1" />
          <label class="save-load-inline-check">
            <input v-model="saveLoadSetting.IsPage" type="checkbox" />
            <span>支持翻页</span>
          </label>
        </div>

        <div class="field-inline save-load-checks">
          <label><input v-model="saveLoadSetting.IsMapName" type="checkbox" /> 显示剧情名</label>
          <label><input v-model="saveLoadSetting.IsDate" type="checkbox" /> 显示日期</label>
          <label><input v-model="saveLoadSetting.IsMinPic" type="checkbox" /> 显示截图</label>
        </div>
      </section>

      <section class="setting-card">
        <div class="save-load-button-grid">
          <label class="field-block compact">
            <span class="field-label">存档按钮</span>
            <ImageOptionPickerField
              :model-value="saveLoadSetting.BackButtonEx.Index"
              :items="imageOptionPickerItems"
              :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
              title="选择存档按钮"
              @update:model-value="saveLoadSetting.BackButtonEx.Index = $event"
            />
          </label>

          <label class="field-block compact">
            <span class="field-label">关闭按钮</span>
            <ImageOptionPickerField
              :model-value="saveLoadSetting.CloseButtonEx.Index"
              :items="imageOptionPickerItems"
              :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
              title="选择关闭按钮"
              @update:model-value="saveLoadSetting.CloseButtonEx.Index = $event"
            />
          </label>
        </div>

        <div class="field-inline save-load-inline-axis">
          <span class="field-label">关闭按钮坐标</span>
          <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
            <NumberStepperField v-model="saveLoadSetting.CloseButtonEx.X" class="cell-input sm" />
            <NumberStepperField v-model="saveLoadSetting.CloseButtonEx.Y" class="cell-input sm" />
          </div>
        </div>
      </section>

      <section class="setting-card">
        <div class="save-load-font-grid">
          <label class="field-block compact">
            <span class="field-label">存档字体</span>
            <FontSelectField
              :model-value="saveLoadSetting.Font"
              picker-title="选择存读档字体"
              @update:model-value="updateSaveLoadFontPath"
            />
          </label>

          <label class="field-block compact">
            <span class="field-label">字体大小</span>
            <NumberStepperField
              :model-value="saveLoadStepperDisplaySize"
              class="cell-input sm"
              :min="SAVE_LOAD_FONT_MIN_SIZE"
              @update:model-value="updateSaveLoadFontSize"
            />
          </label>
        </div>
      </section>

      <section class="setting-card">
        <div class="card-title">存档内容显示区域</div>
        <div class="save-load-content-grid">
          <div class="field-inline save-load-inline-axis">
            <span class="field-label">最大列数</span>
            <NumberStepperField v-model="saveLoadSetting.Column" class="cell-input sm save-load-single-stepper" :min="1" />
          </div>

          <div class="save-load-stacked-field">
            <span class="field-label">行列间距</span>
            <div class="save-load-button-grid">
              <NumberStepperField v-model="saveLoadSetting.SpanRow" class="cell-input sm" :min="1" />
              <NumberStepperField v-model="saveLoadSetting.SpanCol" class="cell-input sm" :min="1" />
            </div>
          </div>

          <div class="save-load-stacked-field">
            <span class="field-label">原点坐标</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.Viewport.X" class="cell-input sm" />
              <NumberStepperField v-model="saveLoadSetting.Viewport.Y" class="cell-input sm" />
            </div>
          </div>

          <div class="save-load-stacked-field">
            <span class="field-label">限制大小</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.Viewport.Width" class="cell-input sm" :min="1" />
              <NumberStepperField v-model="saveLoadSetting.Viewport.Height" class="cell-input sm" :min="1" />
            </div>
          </div>
        </div>
      </section>

      <section class="setting-card">
        <div class="card-title">存档内容设置</div>

        <div class="save-load-content-axis-stack">
          <div class="field-inline save-load-inline-axis">
            <span class="field-label">剧情名坐标</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.Name_X" class="cell-input sm" />
              <NumberStepperField v-model="saveLoadSetting.Name_Y" class="cell-input sm" />
            </div>
          </div>
          <div class="field-inline save-load-inline-axis">
            <span class="field-label">时间坐标</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.Date_X" class="cell-input sm" />
              <NumberStepperField v-model="saveLoadSetting.Date_Y" class="cell-input sm" />
            </div>
          </div>
          <div class="field-inline save-load-inline-axis">
            <span class="field-label">截图坐标</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.Pic_X" class="cell-input sm" />
              <NumberStepperField v-model="saveLoadSetting.Pic_Y" class="cell-input sm" />
            </div>
          </div>
          <div class="field-inline save-load-inline-axis">
            <span class="field-label">截图缩放率</span>
            <NumberStepperField v-model="saveLoadSetting.Zoom" class="cell-input sm save-load-single-stepper" :min="1" />
          </div>
        </div>
      </section>

      <section class="setting-card">
        <div class="card-title">按钮翻页设置</div>

        <div class="save-load-paging-grid" :class="{ 'save-load-paging-grid--disabled': !saveLoadSetting.IsPage }">
          <div class="save-load-button-grid">
            <label class="field-block compact">
              <span class="field-label">上一页按钮</span>
              <ImageOptionPickerField
                :model-value="saveLoadSetting.PrePic.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                :disabled="!saveLoadSetting.IsPage"
                title="选择上一页按钮"
                @update:model-value="saveLoadSetting.PrePic.Index = $event"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">下一页按钮</span>
              <ImageOptionPickerField
                :model-value="saveLoadSetting.NextPic.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                :disabled="!saveLoadSetting.IsPage"
                title="选择下一页按钮"
                @update:model-value="saveLoadSetting.NextPic.Index = $event"
              />
            </label>
          </div>

          <div class="save-load-paging-axis-stack">
            <div class="field-inline save-load-inline-axis">
              <span class="field-label">上一页坐标</span>
              <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                <NumberStepperField v-model="saveLoadSetting.PrePic.X" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                <NumberStepperField v-model="saveLoadSetting.PrePic.Y" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
              </div>
            </div>

            <div class="field-inline save-load-inline-axis">
              <span class="field-label">下一页坐标</span>
              <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                <NumberStepperField v-model="saveLoadSetting.NextPic.X" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                <NumberStepperField v-model="saveLoadSetting.NextPic.Y" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
              </div>
            </div>
          </div>

          <div class="field-inline save-load-inline-axis">
            <span class="field-label">页码坐标</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.PageX" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
              <NumberStepperField v-model="saveLoadSetting.PageY" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <div v-else class="settings-page save-load-page">
    <div class="save-load-grid">
      <div class="save-load-left-column">
        <section class="setting-card">
          <label class="field-block save-load-inline-field">
            <span class="field-label save-load-inline-label">背景图</span>
            <ResourceSelectField
              :model-value="saveLoadSetting.BackPic.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择存读档背景图"
              @update:model-value="updateSaveLoadBackPic"
            />
          </label>

          <div class="field-inline save-load-summary-row">
            <span class="field-label">最大存档数</span>
            <NumberStepperField v-model="saveLoadSetting.Max" class="cell-input sm save-load-inline-stepper" :min="1" />
            <label class="save-load-inline-check">
              <input v-model="saveLoadSetting.IsPage" type="checkbox" />
              <span>支持翻页</span>
            </label>
          </div>

          <div class="field-inline save-load-checks">
            <label><input v-model="saveLoadSetting.IsMapName" type="checkbox" /> 显示剧情名</label>
            <label><input v-model="saveLoadSetting.IsDate" type="checkbox" /> 显示日期</label>
            <label><input v-model="saveLoadSetting.IsMinPic" type="checkbox" /> 显示截图</label>
          </div>
        </section>

        <section class="setting-card">
          <div class="save-load-button-grid">
            <label class="field-block compact">
              <span class="field-label">存档按钮</span>
              <ImageOptionPickerField
                :model-value="saveLoadSetting.BackButtonEx.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                title="选择存档按钮"
                @update:model-value="saveLoadSetting.BackButtonEx.Index = $event"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">关闭按钮</span>
              <ImageOptionPickerField
                :model-value="saveLoadSetting.CloseButtonEx.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                title="选择关闭按钮"
                @update:model-value="saveLoadSetting.CloseButtonEx.Index = $event"
              />
            </label>
          </div>

          <div class="field-inline save-load-inline-axis">
            <span class="field-label">关闭按钮坐标</span>
            <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
              <NumberStepperField v-model="saveLoadSetting.CloseButtonEx.X" class="cell-input sm" />
              <NumberStepperField v-model="saveLoadSetting.CloseButtonEx.Y" class="cell-input sm" />
            </div>
          </div>
        </section>

        <section class="setting-card">
          <div class="save-load-font-grid">
            <label class="field-block compact">
              <span class="field-label">存档字体</span>
              <FontSelectField
                :model-value="saveLoadSetting.Font"
                picker-title="选择存读档字体"
                @update:model-value="updateSaveLoadFontPath"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">字体大小</span>
              <NumberStepperField
              :model-value="saveLoadStepperDisplaySize"
              class="cell-input sm"
              :min="SAVE_LOAD_FONT_MIN_SIZE"
              @update:model-value="updateSaveLoadFontSize"
            />
            </label>
          </div>
        </section>

        <section class="setting-card">
          <div class="card-title">存档内容显示区域</div>
          <div class="save-load-content-grid">
            <div class="field-inline save-load-inline-axis">
              <span class="field-label">最大列数</span>
              <NumberStepperField v-model="saveLoadSetting.Column" class="cell-input sm save-load-single-stepper" :min="1" />
            </div>

            <div class="save-load-stacked-field">
              <span class="field-label">行列间距</span>
              <div class="save-load-button-grid">
                <NumberStepperField v-model="saveLoadSetting.SpanRow" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="saveLoadSetting.SpanCol" class="cell-input sm" :min="1" />
              </div>
            </div>

            <div class="save-load-stacked-field">
              <span class="field-label">原点坐标</span>
              <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                <NumberStepperField v-model="saveLoadSetting.Viewport.X" class="cell-input sm" />
                <NumberStepperField v-model="saveLoadSetting.Viewport.Y" class="cell-input sm" />
              </div>
            </div>

            <div class="save-load-stacked-field">
              <span class="field-label">限制大小</span>
              <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                <NumberStepperField v-model="saveLoadSetting.Viewport.Width" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="saveLoadSetting.Viewport.Height" class="cell-input sm" :min="1" />
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="save-load-right-column">
        <div class="save-load-top-grid">
          <section class="setting-card">
            <div class="card-title">存档内容设置</div>

            <div class="save-load-content-axis-stack">
              <div class="field-inline save-load-inline-axis">
                <span class="field-label">剧情名坐标</span>
                <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                  <NumberStepperField v-model="saveLoadSetting.Name_X" class="cell-input sm" />
                  <NumberStepperField v-model="saveLoadSetting.Name_Y" class="cell-input sm" />
                </div>
              </div>
              <div class="field-inline save-load-inline-axis">
                <span class="field-label">时间坐标</span>
                <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                  <NumberStepperField v-model="saveLoadSetting.Date_X" class="cell-input sm" />
                  <NumberStepperField v-model="saveLoadSetting.Date_Y" class="cell-input sm" />
                </div>
              </div>
              <div class="field-inline save-load-inline-axis">
                <span class="field-label">截图坐标</span>
                <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                  <NumberStepperField v-model="saveLoadSetting.Pic_X" class="cell-input sm" />
                  <NumberStepperField v-model="saveLoadSetting.Pic_Y" class="cell-input sm" />
                </div>
              </div>
              <div class="field-inline save-load-inline-axis">
                <span class="field-label">截图缩放率</span>
                <NumberStepperField v-model="saveLoadSetting.Zoom" class="cell-input sm save-load-single-stepper" :min="1" />
              </div>
            </div>
          </section>

          <section class="setting-card">
            <div class="card-title">按钮翻页设置</div>

            <div class="save-load-paging-grid" :class="{ 'save-load-paging-grid--disabled': !saveLoadSetting.IsPage }">
              <div class="save-load-button-grid">
                <label class="field-block compact">
                  <span class="field-label">上一页按钮</span>
                  <ImageOptionPickerField
                    :model-value="saveLoadSetting.PrePic.Index"
                    :items="imageOptionPickerItems"
                    :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                    :disabled="!saveLoadSetting.IsPage"
                    title="选择上一页按钮"
                    @update:model-value="saveLoadSetting.PrePic.Index = $event"
                  />
                </label>

                <label class="field-block compact">
                  <span class="field-label">下一页按钮</span>
                  <ImageOptionPickerField
                    :model-value="saveLoadSetting.NextPic.Index"
                    :items="imageOptionPickerItems"
                    :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                    :disabled="!saveLoadSetting.IsPage"
                    title="选择下一页按钮"
                    @update:model-value="saveLoadSetting.NextPic.Index = $event"
                  />
                </label>
              </div>

              <div class="save-load-paging-axis-stack">
                <div class="field-inline save-load-inline-axis">
                  <span class="field-label">上一页坐标</span>
                  <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                    <NumberStepperField v-model="saveLoadSetting.PrePic.X" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                    <NumberStepperField v-model="saveLoadSetting.PrePic.Y" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                  </div>
                </div>

                <div class="field-inline save-load-inline-axis">
                  <span class="field-label">下一页坐标</span>
                  <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                    <NumberStepperField v-model="saveLoadSetting.NextPic.X" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                    <NumberStepperField v-model="saveLoadSetting.NextPic.Y" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                  </div>
                </div>
              </div>

              <div class="field-inline save-load-inline-axis">
                <span class="field-label">页码坐标</span>
                <div class="field-axis-grid field-axis-grid--plain save-load-inline-axis-grid">
                  <NumberStepperField v-model="saveLoadSetting.PageX" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                  <NumberStepperField v-model="saveLoadSetting.PageY" class="cell-input sm" :disabled="!saveLoadSetting.IsPage" />
                </div>
              </div>
            </div>
          </section>
        </div>

        <section class="setting-card settings-preview-card save-load-preview-card">
          <CanvasPreviewSurface
            host-class="settings-preview-shell save-load-preview-shell"
            stage-class="settings-preview-stage save-load-preview-stage"
            :design-width="screenWidth"
            :design-height="screenHeight"
            :background-url="saveLoadBackgroundUrl"
            background-empty-text="存读档背景图"
            :draw-items="previewDrawItems"
            :hotspot-items="previewHotspotItems"
            :guide-active="previewGuideActive"
            @stage-resize="onPreviewStageResize"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.save-load-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
}

.save-load-left-column,
.save-load-right-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.save-load-left-column {
  overflow-y: auto;
  overflow-x: hidden;
}

.save-load-preview-card {
  min-height: 240px;
}

.save-load-inline-field {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.save-load-inline-label {
  align-self: center;
}

.save-load-summary-row {
  justify-content: flex-start;
  gap: 8px;
  align-items: center;
}

.save-load-inline-stepper {
  width: 88px;
}

.save-load-inline-check {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.save-load-checks {
  gap: 8px 12px;
}

.save-load-inline-axis {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.save-load-inline-axis-grid {
  width: 100%;
}

.save-load-font-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.save-load-content-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.save-load-stacked-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.save-load-content-axis-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.save-load-button-grid,
.save-load-panel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.save-load-single-stepper {
  width: 100%;
  max-width: 120px;
}

.save-load-paging-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.save-load-paging-grid--disabled {
  opacity: 0.7;
}

.save-load-paging-axis-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.save-load-top-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

@media (max-width: 1180px) and (orientation: landscape) {
  .save-load-grid {
    grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  }
}

@media (max-width: 1200px) and (orientation: portrait) {
  .save-load-grid,
  .save-load-top-grid,
  .save-load-panel-grid,
  .save-load-font-grid,
  .save-load-inline-field {
    grid-template-columns: 1fr;
  }
}
</style>
